import { Button, Chip, Stack, Tooltip } from "@mui/material";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import moment from "moment";
import { DbObjectStatusHistoryInfos } from "../../../Core/Api/Types/db-object-status-history-infos";
import { useContext, useEffect, useMemo, useState } from "react";
import { ModalContext } from "../../Modals";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import ModalConfirmAction, {
  ModalConfirmActionElementProps,
} from "../../Modals/ModalConfirmAction/modal-confirm-action";
import { ChangeStatusRequest } from "../../../Core/Api/Types/change-status-request";
import { assetService } from "../../../Core/Api/services";
import { DbStatusTransitionInfos } from "../../../Core/Api/Types/db-status-transition-infos";
import { useTranslation } from "react-i18next";

export interface TableCellAdamStatusHistoryProps {
  data: { history: DbObjectStatusHistoryInfos[] | null; status: ObjectStatus; assetId?: any };
}
const TableCellAdamStatusHistory: React.FC<TableCellAdamStatusHistoryProps> = ({ data }) => {
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };
  const enqueueCustomSnackbar = useCustomSnackbar();
  const [allowedTransitions, setAllowedTransitions] = useState<DbStatusTransitionInfos[]>([]);

  useEffect(() => {
    data?.assetId &&
      assetService.GetStatusTransitions(data.assetId).then((response) => {
        if (response.success && response.transitions) {
          setAllowedTransitions(response.transitions);
        }
      });
  }, [data?.assetId]);

  const statusHistory = useMemo(() => {
    if (!data.history) return [];
    return [...data.history].sort((a, b) =>
      (a.date ?? 0) < (b.date ?? 0) ? -1 : (a.date ?? 0) === (b.date ?? 0) ? 0 : 1
    );
  }, [data?.assetId, data.history]);

  const canReject = useMemo(() => {
    if (data.status === ObjectStatus.NeedCheckOrEditing) {
      return (
        allowedTransitions.find(
          (x) => x.inputStatus === ObjectStatus.NeedCheckOrEditing && x.outputStatuses?.includes(ObjectStatus.Rejected)
        ) !== undefined
      );
    } else if (data.status === ObjectStatus.Accepted) {
      return (
        allowedTransitions.find(
          (x) =>
            x.inputStatus === ObjectStatus.Accepted && x.outputStatuses?.includes(ObjectStatus.RejectedForBroadcast)
        ) !== undefined
      );
    }
  }, [data?.assetId, allowedTransitions]);
  if (!data) return <></>;

  if (!statusHistory || data.status === ObjectStatus.Undefined || data.status === ObjectStatus.New)
    return <Chip label="En attente de réception" color="info" variant="outlined" size="small" />;

  const spliceFromIndex = statusHistory.findLastIndex(
    (x) => x.status === ObjectStatus.RejectedAuto || x.status === ObjectStatus.NeedCheckOrEditing
  );

  const objectStatus =
    spliceFromIndex > 0 ? [...statusHistory.slice(spliceFromIndex, statusHistory.length)] : [...statusHistory];
  const currentStatus: ObjectStatus | undefined | null = data.status;
  const parsedData = {
    HAB:
      currentStatus === ObjectStatus.RejectedAuto
        ? ["error", objectStatus.filter((x) => x.status === ObjectStatus.RejectedAuto)?.pop() ?? {}]
        : objectStatus.find((x) => x.status === ObjectStatus.NeedCheckOrEditing) !== undefined
        ? ["success", objectStatus.filter((x) => x.status === ObjectStatus.NeedCheckOrEditing).pop()]
        : ["default", null],
    PAV:
      currentStatus === ObjectStatus.RejectedAuto || currentStatus === ObjectStatus.NeedCheckOrEditing
        ? ["default", null]
        : objectStatus.find((x) => x.status === ObjectStatus.Accepted) !== undefined
        ? ["success", objectStatus.filter((x) => x.status === ObjectStatus.Accepted).pop()]
        : objectStatus.find((x) => x.status === ObjectStatus.Rejected) !== undefined
        ? ["error", objectStatus.filter((x) => x.status === ObjectStatus.Rejected).pop()]
        : ["default", null],
    PAD:
      currentStatus === ObjectStatus.Rejected
        ? ["default", null]
        : currentStatus === ObjectStatus.RejectedAuto || currentStatus === ObjectStatus.NeedCheckOrEditing
        ? ["default", null]
        : objectStatus.find((x) => x.status === ObjectStatus.AcceptedForBroadcast) !== undefined
        ? ["success", objectStatus.filter((x) => x.status === ObjectStatus.AcceptedForBroadcast).pop()]
        : objectStatus.find((x) => x.status === ObjectStatus.RejectedForBroadcast) !== undefined
        ? [
            "error",
            objectStatus
              .filter(
                (x) =>
                  x.status === ObjectStatus.RejectedForBroadcast &&
                  (objectStatus.findLast((y) => y.status === ObjectStatus.Accepted)?.date ?? 0) < (x.date ?? 0) // to avoid display when asset is reingested.
              )
              .pop(),
          ]
        : ["default", null],
    LIVR:
      currentStatus === ObjectStatus.RejectedForBroadcast
        ? ["default", null]
        : currentStatus === ObjectStatus.Rejected
        ? ["default", null]
        : currentStatus === ObjectStatus.RejectedAuto || currentStatus === ObjectStatus.NeedCheckOrEditing
        ? ["default", null]
        : currentStatus === ObjectStatus.Delivered
        ? ["success", objectStatus.filter((x) => x.status === ObjectStatus.Delivered)?.pop() ?? {}]
        : objectStatus.find((x) => x.status === ObjectStatus.Delivered) !== undefined
        ? ["success", objectStatus.filter((x) => x.status === ObjectStatus.Delivered).pop()]
        : ["default", null],
    DIFF:
      currentStatus === ObjectStatus.RejectedForBroadcast
        ? ["default", null]
        : currentStatus === ObjectStatus.Rejected
        ? ["default", null]
        : currentStatus === ObjectStatus.RejectedAuto || currentStatus === ObjectStatus.NeedCheckOrEditing
        ? ["default", null]
        : currentStatus === ObjectStatus.Delivered
        ? ["warning", objectStatus.filter((x) => x.status === ObjectStatus.Delivered)?.pop() ?? {}]
        : objectStatus.find((x) => x.status === ObjectStatus.Broadcasted) !== undefined
        ? ["success", objectStatus.filter((x) => x.status === ObjectStatus.Broadcasted).pop()]
        : ["default", null],
  };

  const canRejectPav =
    data.assetId &&
    canReject &&
    parsedData.HAB &&
    parsedData.HAB[0] &&
    parsedData.HAB[0] == "success" &&
    parsedData.PAV &&
    parsedData.PAV[0] &&
    parsedData.PAV[0] === "default";
  const canRejectPad =
    data.assetId &&
    canReject &&
    parsedData.PAV &&
    parsedData.PAV[0] &&
    parsedData.PAV[0] == "success" &&
    parsedData.PAD &&
    parsedData.PAD[0] &&
    parsedData.PAD[0] === "default";

  const handleRejectClick = () => {
    if (currentStatus === ObjectStatus.NeedCheckOrEditing) {
      if (!openModal) return;

      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: true,
        commentLabel: t("modal:body.letUsMessageToExplainYourSeason"),
        title: t("modal:title.rejectQuestionMark"),
        onConfirm: (comment) => {
          let request = new ChangeStatusRequest({
            status: ObjectStatus.Rejected,
            message: comment,
            objectId: data.assetId,
          });
          assetService.ChangeStatus(request).then((response) => {
            if (response.success) {
              enqueueCustomSnackbar(t("alert:success.statusChangedAndObjectRejected"), "success");
            } else {
              enqueueCustomSnackbar(response.message ?? t("alert:error.errorWhileChangingStatus"), "error");
            }
          });
        },
      });
    } else if (currentStatus === ObjectStatus.Accepted) {
      if (!openModal) return;

      openModal<ModalConfirmActionElementProps>(ModalConfirmAction, {
        displayComment: true,
        commentLabel: t("modal:body.letUsMessageToExplainYourSeason"),
        title: t("modal:title.rejectQuestionMark"),
        onConfirm: (comment) => {
          let request = new ChangeStatusRequest({
            status: ObjectStatus.RejectedForBroadcast,
            message: comment,
            objectId: data.assetId,
          });
          assetService.ChangeStatus(request).then((response) => {
            if (response.success) {
              enqueueCustomSnackbar(t("alert:success.statusChangedAndObjectRejectedForBroadcast"), "success");
            } else {
              enqueueCustomSnackbar(response.message ?? t("alert:error.errorWhileChangingStatus"), "error");
            }
          });
        },
      });
    }
  };

  return (
    <Stack direction={"row"}>
      {Object.keys(parsedData).map((x, i) =>
        x === "PAD" && canRejectPad ? (
          <Button onClick={handleRejectClick} sx={{ height: "100%", maxHeight: "24px", mr: "4px" }} size="small">
            {t("common:reject")}
          </Button>
        ) : x === "PAV" && canRejectPav ? (
          <Button onClick={handleRejectClick} sx={{ height: "100%", maxHeight: "24px", mr: "4px" }} size="small">
            {t("common:reject")}
          </Button>
        ) : (
          <Tooltip
            key={i}
            title={
              parsedData[x][1]
                ? `${parsedData[x][1].date ? moment(parsedData[x][1].date).format("DD/MM/YYYY HH:mm") : ""} ${
                    parsedData[x][1].comment ? ` - ${parsedData[x][1].comment}` : ""
                  }
            `
                : ""
            }
          >
            <Chip sx={{ maxHeight: "24px", mr: 0.5 }} label={x} color={parsedData[x][0]} />
          </Tooltip>
        )
      )}
    </Stack>
  );
};
export default TableCellAdamStatusHistory;
